import { useEffect, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, Check, LoaderCircle, Send } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { StaffAssignmentPicker } from '@/components/staff-assignment-picker'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { TooltipProvider } from '@/components/ui/tooltip'
import { cn, formatMoney } from '@/lib/utils'
import { useCachedQueryData } from '../api/cached'
import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import { invalidateQuoteOverview } from '../api/queries'
import type { Quote } from '../api/types'
import { invoicingQueryKey, type InvoicingState } from '../invoicing/api'
import { finalQuoteProblem } from '../invoicing/final-quote'
import { InvoicingTab } from '../invoicing/invoicing-tab'
import {
  newRequestId,
  publicationsQueryOptions,
  publishingApi,
  quoteActivityQueryOptions,
  quoteCommentsQueryOptions,
  type PublicationListItem,
} from '../publishing/api'
import { PublishQuoteAction } from '../publishing/publish-quote'
import { assignProjectStaff, reviewQueryKey, reviewQueryOptions, type ProjectStaff } from '../review/review-api'
import { numberValue } from '../ui/numbers'
import { SurfaceCard } from '../ui/page'
import type { WorkflowTabProps } from '../workflow/types'
import { invalidateAfterStart, projectStartQueryOptions, type ProjectStartBlocker } from './api'

// The Start stage (USA-1333, USA-1334): what is left before the project starts, the final quote
// and the team; the invoices beside it. There is no Start button: the project starts on its own.

const ROLES: ReadonlyArray<readonly [keyof ProjectStaff, string]> = [
  ['sales', 'Sales'],
  ['operations', 'Ops'],
  ['warehousing', 'Warehouse'],
  ['accounting', 'Accounting'],
]

const PAYMENT_CODES = new Set<ProjectStartBlocker['code']>([
  'financing_terms_required',
  'invoice_required',
  'deposit_payment_required',
  'bank_payment_clearing',
])
const STEP_CODES = new Set<ProjectStartBlocker['code']>([
  'current_final_publication_required',
  'customer_acceptance_required',
  ...PAYMENT_CODES,
])

const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
const dayTime = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

function plural(count: number, word: string) {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}

/** One thing a start waits on: done, or what it needs, with the control that does it. */
function Step({
  done,
  title,
  children,
  actions,
}: {
  done: boolean
  title: string
  children?: ReactNode
  actions?: ReactNode
}) {
  return (
    <li className="flex gap-3 px-4 py-3" data-start-step={title} data-start-step-done={done || undefined}>
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border',
          done ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-border bg-background',
        )}
      >
        {done ? <Check className="size-3" /> : null}
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <p className="text-sm font-medium">
            <span className="sr-only">{done ? 'Done: ' : 'To do: '}</span>
            {title}
          </p>
          {children ? <div className="mt-0.5 text-sm text-muted-foreground">{children}</div> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </li>
  )
}

/** The final quote's row: published or not, sent, viewed and commented on, with send and details. */
function FinalQuote({
  quote,
  done,
  publication,
  problem,
  publishReady,
  publishBlockedBy,
}: {
  quote: Quote
  done: boolean
  publication: PublicationListItem | undefined
  /** Why the customer cannot sign it now, when known (USA-1336). */
  problem: string | null
  publishReady: boolean
  publishBlockedBy: string | undefined
}) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const activity = useQuery(quoteActivityQueryOptions(api, quote.id))
  const comments = useQuery(quoteCommentsQueryOptions(api, quote.id))
  const number = publication?.publication_number
  const events = (activity.data ?? []).filter(
    (item) => (item.payload as { publication_number?: number } | null)?.publication_number === number,
  )
  const sent = events.filter((item) => item.activity_type === 'quote_sent')
  const lastSent = sent.map((item) => item.created_at).sort().at(-1)
  const viewed = events.some((item) => item.activity_type === 'customer_opened')
  const commentCount = (comments.data ?? []).filter((item) => item.publication_number === number).length
  const send = useMutation({
    retry: false,
    mutationFn: () => publishingApi.send(api, quote, number!, newRequestId()),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['quote-activity', quote.id] }),
  })
  const publish = (
    <PublishQuoteAction
      disabled={!publishReady}
      disabledReason={publishBlockedBy}
      publicationMode="final"
      quote={quote}
      triggerLabel="Publish final quote"
    />
  )

  if (!done) {
    // Never published as final, a draft published after it, the quote changed, or changes asked for.
    const stale = publication === undefined ? false : !publication.current
    return (
      <Step actions={publish} done={false} title="Final quote">
        {!publishReady && publishBlockedBy
          ? publishBlockedBy
          : (problem ?? (stale ? 'The quote changed after it was published. Publish it again.' : 'Not published yet.'))}
      </Step>
    )
  }
  const facts = [
    `#${number}`,
    // Say nothing about sending until the activity has loaded, rather than "not sent" by mistake.
    activity.isPending ? null : lastSent ? `Sent ${day.format(new Date(lastSent))}` : 'Not sent yet',
    viewed ? 'Viewed' : null,
    commentCount ? plural(commentCount, 'comment') : null,
  ].filter(Boolean)
  return (
    <Step
      actions={
        <>
          <LoadingButton
            loading={send.isPending}
            loadingText="Sending..."
            onClick={() => send.mutate()}
            size="sm"
            variant="outline"
          >
            <Send aria-hidden="true" />
            {sent.length ? 'Resend' : 'Send to customer'}
          </LoadingButton>
          <Button asChild size="sm" variant="ghost">
            <Link params={{ quoteId: quote.id }} to="/quotes/$quoteId/share">
              Details
            </Link>
          </Button>
        </>
      }
      done
      title="Final quote"
    >
      {facts.join(' · ')}
      {send.isError ? (
        <span className="block text-destructive" role="alert">
          {send.error instanceof QuoteBuilderApiError && send.error.code === 'customer_email_required'
            ? 'Add the customer’s email before sending.'
            : 'The email could not be sent. Try again.'}
        </span>
      ) : null}
    </Step>
  )
}

/** Who the project goes to. Not a quote change: setting it keeps the final quote current. */
function ProjectTeam({ quoteId }: { quoteId: string }) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const review = useQuery(reviewQueryOptions(api, quoteId))
  const assign = useMutation({
    mutationFn: ({ role, userId }: { role: keyof ProjectStaff; userId: number | null }) =>
      assignProjectStaff(api, quoteId, role, userId),
    onSuccess: (next) => client.setQueryData(reviewQueryKey(quoteId), next),
  })
  return (
    <SurfaceCard title="Project team">
      <TooltipProvider>
        <div aria-label="Project team" className="flex flex-wrap items-center gap-1.5 px-4 py-3" role="group">
          {ROLES.map(([role, label]) => (
            <StaffAssignmentPicker
              currentUserId={review.data?.staff[role]?.id ?? null}
              disabled={!review.data}
              key={role}
              label={label}
              onChange={(user) => assign.mutate({ role, userId: user?.id ?? null })}
              pending={assign.isPending && assign.variables?.role === role}
            />
          ))}
        </div>
      </TooltipProvider>
      {assign.isError ? (
        <p className="border-t px-4 py-2 text-xs text-destructive" role="alert">
          The team was not saved. Try again.
        </p>
      ) : null}
    </SurfaceCard>
  )
}

/** What is left before the project starts; it starts on its own once nothing is. */
function StartChecklist({ quote, active }: { quote: Quote; active: boolean }) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const start = useQuery({
    ...projectStartQueryOptions(api, quote.id),
    enabled: active,
    // The start happens in the background once the last step is done.
    refetchInterval: (query) => (query.state.data?.state === 'started' ? false : 10_000),
  })
  const publications = useQuery({ ...publicationsQueryOptions(api, quote.id), enabled: active })
  const review = useQuery({ ...reviewQueryOptions(api, quote.id), enabled: active })
  // The invoices beside it own this read; follow it without taking over its fetch.
  const invoicingData = useCachedQueryData<InvoicingState>(invoicingQueryKey(quote.id))
  const read = start.data

  // Publishing, sending and payments change what is left: read it again.
  const refetchStart = start.refetch
  useEffect(() => {
    if (active) void refetchStart()
  }, [active, publications.dataUpdatedAt, invoicingData, refetchStart])
  useEffect(() => {
    if (read?.state === 'started') {
      void invalidateAfterStart(client, quote.id)
      void invalidateQuoteOverview(client)
    }
  }, [client, quote.id, read?.state])

  if (!read) {
    return (
      <SurfaceCard title="Project start">
        <p className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground" role="status">
          <LoaderCircle aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
          Checking what is left
        </p>
      </SurfaceCard>
    )
  }
  const codes = new Set(read.blockers.map((item) => item.code))
  const finalQuoteDone = !codes.has('current_final_publication_required')
  const finals = (publications.data ?? []).filter((item) => item.mode === 'final')
  const latestFinal = finals.sort((a, b) => b.publication_number - a.publication_number)[0]
  const signature = latestFinal?.current ? latestFinal.signature : null
  const signed = finalQuoteDone && !codes.has('customer_acceptance_required')
  const paymentBlocker = read.blockers.find((item) => PAYMENT_CODES.has(item.code))
  const others = read.blockers.filter((item) => !STEP_CODES.has(item.code))
  const invoice = invoicingData?.invoice
  const paid = numberValue(invoicingData?.paid ?? 0)
  const ready = read.state === 'ready'

  return (
    <SurfaceCard
      actions={
        ready ? (
          <StatusPill tone="progress">
            <LoaderCircle aria-hidden="true" className="mr-1 size-3 animate-spin motion-reduce:animate-none" />
            Starting
          </StatusPill>
        ) : (
          <StatusPill tone="neutral">{plural(read.blockers.length, 'step')} left</StatusPill>
        )
      }
      data-project-start={read.state}
      title="Project start"
    >
      <ul className="divide-y">
        <FinalQuote
          done={finalQuoteDone}
          problem={finalQuoteProblem(invoicingData?.final_quote)}
          publication={latestFinal}
          publishBlockedBy={review.data?.publication_errors[0]}
          publishReady={!!review.data?.publication_ready}
          quote={quote}
        />
        <Step done={signed} title="Customer signs">
          {signed && signature
            ? `${signature.signer_name} · ${dayTime.format(new Date(signature.signed_at))}`
            : finalQuoteDone
              ? 'Waiting for the customer to sign the final quote.'
              : 'After the final quote is published.'}
        </Step>
        <Step done={!paymentBlocker} title="Payment">
          {paymentBlocker
            ? paymentBlocker.message
            : paid > 0
              ? `Paid ${formatMoney(paid)}`
              : read.payment_rule?.startsWith('Prepaid')
                ? 'Paid separately'
                : 'Invoice issued'}
          {paymentBlocker && invoice?.sent_at && invoice.status === 'open' ? (
            <span className="block">Invoice sent {day.format(new Date(invoice.sent_at))}; awaiting payment.</span>
          ) : null}
        </Step>
        {others.map((item) => (
          <li className="flex gap-3 px-4 py-3 text-sm" key={item.message}>
            <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-amber-600" />
            {item.message}
          </li>
        ))}
      </ul>
      <p className="border-t bg-muted/30 px-4 py-2.5 text-xs text-muted-foreground">
        The project starts on its own once these are done, with its POs held for your review.
      </p>
    </SurfaceCard>
  )
}

/** The Start stage: what is left before the project starts, the team, and the invoices. */
export function StartTab(props: WorkflowTabProps) {
  const { quote, active = true } = props
  // Once started, the project panel above says where it stands; the invoices stay for payments.
  if (quote.status === 'started') return <InvoicingTab {...props} />
  return (
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)]" data-start-stage={quote.id}>
      <div className="grid min-w-0 gap-4">
        <StartChecklist active={active} quote={quote} />
        <ProjectTeam quoteId={quote.id} />
      </div>
      <InvoicingTab {...props} />
    </div>
  )
}
